import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import AuthService from '../services/AuthService';
import ImageCacheAPIService from '../services/ImageCacheAPIService';
import ApiConfig from '../services/ApiConfig';

const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
    const [settings, setSettings] = useState({
        storeName: "Dandi´s",
        logo: null,
        enableTips: true,
        enableComments: true,
        defaultView: 'products',
        button_color: '#E53935',
        background_color: '#D32F2F',
        primary_color: '#C62828',
        secondary_color: '#FF6F61',
        header_color: '#B71C1C',
        card_color: '#ffffff',
        box_color: '#FFCDD2',
    });

    const [sessionData, setSessionData] = useState(null);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [authenticatedUser, setAuthenticatedUser] = useState(null);
    const [categoryImages, setCategoryImages] = useState({});
    const [orderType, setOrderType] = useState(null);

    const updateSettings = useCallback((newSettings) => {
        if (newSettings.apiUrl) ApiConfig.setBaseUrl(newSettings.apiUrl);
        setSettings(prev => ({ ...prev, ...newSettings }));
        Object.entries(newSettings).forEach(([key, value]) => {
            if (['button_color', 'background_color', 'primary_color', 'secondary_color', 'header_color', 'card_color', 'box_color'].includes(key)) {
                document.documentElement.style.setProperty(`--${key}`, value);
                console.log(`Applied CSS variable --${key}: ${value}`);
            } else if (key === 'custom_store_name') {
                setSettings(prev => ({ ...prev, storeName: value }));
            } else if (key === 'logo_image') {
                if (value) {
                    let mimeType = 'image/jpeg';
                    if (value.startsWith('iVBORw0KGgo')) {
                        mimeType = 'image/png';
                    } else if (value.startsWith('/9j/')) {
                        mimeType = 'image/jpeg';
                    }
                    setSettings(prev => ({ ...prev, logo: `data:${mimeType};base64,${value}` }));
                } else {
                    setSettings(prev => ({ ...prev, logo: null }));
                }
            }
        });
    }, []);

    const updateSessionData = useCallback((data) => {
        console.log('╔═══════════════════════════════════════════════════════════╗');
        console.log('║         DATOS RECIBIDOS DE ODOO (LOGIN)                  ║');
        console.log('╚═══════════════════════════════════════════════════════════╝');
        console.log('sessionData completo:', JSON.stringify(data, null, 2));
        console.log('');
        console.log('Datos de empresa extraídos:');
        console.log('  - name:', data.company_data?.name);
        console.log('  - vat:', data.company_data?.vat);
        console.log('  - street:', data.company_data?.street);
        console.log('  - city:', data.company_data?.city);
        console.log('  - turn:', data.company_data?.turn);
        console.log('  - acteco:', data.company_data?.acteco);
        console.log('═══════════════════════════════════════════════════════════');

        setSessionData(data);
        setProducts(data.products || []);
        setCategories(data.categories || []);

        // ── LOG PRODUCTOS Y CATEGORÍAS ─────────────────────────
        const prods = data.products || [];
        const cats = data.categories || [];
        console.log('═══════════ CATEGORÍAS CARGADAS (' + cats.length + ') ═══════════');
        cats.forEach(c => console.log(`  [${c.id}] ${c.name}`));
        console.log('═══════════ PRODUCTOS CARGADOS (' + prods.length + ') ════════════');
        prods.forEach(p => console.log(`  [${p.id}] ${p.name} | cat_id=${p.category_id} cat=${p.category_name} | subcat_id=${p.subcategory_id ?? '-'} subcat=${p.subcategory_name ?? '-'} | precio=${p.price}`));
        console.log('═══════════════════════════════════════════════════════════');
        setPaymentMethods(data.payment_methods || []);

        if (data?.products) {
            ImageCacheAPIService.cacheProducts(data.products)
                .then(result => {
                    console.log(`Cache API: ${result.success} imagenes guardadas en SQLite`);
                    if (result.errors > 0) {
                        console.warn(`Cache API: ${result.errors} imagenes fallaron`);
                    }
                })
                .catch(error => {
                    console.error('Error cacheando imagenes:', error);
                });

            ImageCacheAPIService.fetchAndCacheBanners(data.products)
                .then(result => {
                    console.log(`Banners: ${result.success} cacheados, ${result.skipped} sin banner`);
                })
                .catch(error => {
                    console.error('Error fetching banners:', error);
                });
        }

        if (data?.categories) {
            const allCatData = [
                ...(data.categories || []),
                ...(data.subcategories || []),
            ];
            const map = {};
            allCatData.forEach(cat => {
                if (cat.category_image) {
                    map[cat.id] = cat.category_image;
                }
            });
            console.log('[IMG] categorías con imagen:', Object.keys(map).length, '→', Object.entries(map).map(([id, v]) => `${id}:${v?.length ?? 'null'}`));
            setCategoryImages(map);

            ImageCacheAPIService.cacheCategories(allCatData).catch(() => {});
        }
    }, []);

    const updateAuthenticatedUser = useCallback((userData) => {
        console.log('Updating authenticated user:', userData);
        setAuthenticatedUser(userData);
        if (userData) {
            AuthService.saveUser(userData);
        }
    }, []);

    const clearAuthenticatedUser = useCallback(() => {
        console.log('Clearing authenticated user');
        setAuthenticatedUser(null);
        AuthService.logout();
    }, []);

    const clearSessionData = useCallback(() => {
        setSessionData(null);
        setProducts([]);
        setCategories([]);
        setPaymentMethods([]);
        setCategoryImages({});
        clearAuthenticatedUser();
    }, [clearAuthenticatedUser]);

    useEffect(() => {
        const savedUser = AuthService.getUser();
        if (savedUser) {
            console.log('Usuario autenticado encontrado en localStorage:', savedUser);
            setAuthenticatedUser(savedUser);
        }
    }, []);

    useEffect(() => {
        console.log('Aplicando colores corporativos (tema rojo)...');
        Object.entries(settings).forEach(([key, value]) => {
            if (['button_color', 'background_color', 'primary_color', 'secondary_color', 'header_color', 'card_color', 'box_color'].includes(key)) {
                document.documentElement.style.setProperty(`--${key}`, value);
            }
        });
        console.log('Colores rojos aplicados');
    }, [settings]);

    return (
        <AppContext.Provider
            value={{
                settings,
                updateSettings,
                sessionData,
                updateSessionData,
                clearSessionData,
                products,
                categories,
                paymentMethods,
                categoryImages,
                authenticatedUser,
                updateAuthenticatedUser,
                clearAuthenticatedUser,
                orderType,
                setOrderType,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};